import { useState } from 'react';
import { Tag, Pencil, Trash2, Plus, X, Check, FolderOpen } from 'lucide-react';
import { groupsAPI } from '../api';
import './GroupManager.css';

function GroupManager({ groups, onGroupsChange, onClose }) {
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupType, setNewGroupType] = useState('default');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const groupList = Object.entries(groups);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    setIsCreating(true);
    setError('');
    try {
      const id = newGroupName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      if (!id) { setError('Некорректное имя группы'); return; }
      if (groups[id]) { setError('Группа с таким ID уже существует'); return; }
      await groupsAPI.create(id, newGroupName.trim(), newGroupType);
      setNewGroupName('');
      setNewGroupType('default');
      onGroupsChange();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка создания группы');
    } finally {
      setIsCreating(false);
    }
  };

  const handleTypeChange = async (id, type) => {
    try {
      await groupsAPI.setType(id, type);
      onGroupsChange();
    } catch (err) {
      setError('Ошибка смены типа группы');
    }
  };

  const handleRenameSubmit = async (id) => {
    if (!renameValue.trim()) return;
    try {
      await groupsAPI.rename(id, renameValue.trim());
      setRenamingId(null);
      onGroupsChange();
    } catch (err) {
      setError('Ошибка переименования');
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Удалить группу "${name}"? Файлы станут "Без группы".`)) return;
    try {
      await groupsAPI.delete(id);
      onGroupsChange();
    } catch (err) {
      setError('Ошибка удаления группы');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content group-manager-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><FolderOpen size={16} /> Управление группами</h2>
          <button onClick={onClose} className="modal-close"><X size={14} /></button>
        </div>

        {error && <div className="alert alert-error" style={{ margin: '0 20px 0' }}>{error}</div>}

        <div className="modal-body">
          {groupList.length === 0 ? (
            <p className="group-manager-empty">Групп пока нет. Создайте первую!</p>
          ) : (
            <div className="group-manager-list">
              {groupList.map(([id, group]) => (
                <div key={id} className="group-manager-item">
                  {renamingId === id ? (
                    <div className="group-rename-form">
                      <input
                        type="text"
                        className="input"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRenameSubmit(id);
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                        autoFocus
                      />
                      <button className="btn btn-success btn-sm" onClick={() => handleRenameSubmit(id)}>
                        <Check size={13} />
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setRenamingId(null)}>
                        <X size={13} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="group-manager-row">
                        <span className="group-manager-name">
                          <Tag size={13} className="group-manager-name-icon" />
                          <span className="group-manager-name-text">{group.name}</span>
                        </span>
                        <div className="group-manager-actions">
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => { setRenamingId(id); setRenameValue(group.name); }}
                            title="Переименовать"
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(id, group.name)}
                            title="Удалить"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                      <select
                        className={`group-type-select group-type-${group.type || 'default'}`}
                        value={group.type || 'default'}
                        onChange={(e) => handleTypeChange(id, e.target.value)}
                        title="Тип группы"
                      >
                        <option value="default">Default — 2 VPS</option>
                        <option value="vip">VIP — 5 VPS</option>
                      </select>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleCreate} className="group-create-form">
            <span className="form-label">Новая группа</span>
            <input
              type="text"
              className="input"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Название группы..."
              disabled={isCreating}
            />
            <div className="group-create-row">
              <select
                className={`group-type-select group-type-${newGroupType}`}
                value={newGroupType}
                onChange={(e) => setNewGroupType(e.target.value)}
                disabled={isCreating}
              >
                <option value="default">Default — 2 VPS</option>
                <option value="vip">VIP — 5 VPS</option>
              </select>
              <button type="submit" className="btn btn-success" disabled={isCreating || !newGroupName.trim()}>
                <Plus size={14} /> {isCreating ? '...' : 'Создать'}
              </button>
            </div>
          </form>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Закрыть</button>
        </div>
      </div>
    </div>
  );
}

export default GroupManager;
